import {
  Button,
  Callout,
  CalloutTitle,
  DataTable,
  Icon,
  PageSection,
  SegmentedControl,
  SegmentedControlItem,
  Spacer,
  Stack,
  TextTitle,
} from '@pypestream/design-system';
import {
  TranslationComponent,
  type TranslationKeys,
} from '@pypestream/translations';
import { ColumnDef } from '@tanstack/react-table';
import { html } from 'lit/static-html.js';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import {
  useDeleteLiveAgentPlatformModal,
  useLiveAgentPlatformModal,
} from '../../components/modals';
import { SettingsEmptyState } from '../../components/settings-empty-state';
import {
  renderSolutionChannelGroups,
  SOLUTION_GROUPS_TEST_ID_PREFIX,
  useChannelGroupingResolver,
  useSolutionGroupsTranslate,
} from '../../components/solution-channel-groups';
import { SettingsSectionLoader } from '../../components/settings-section-loader';
import { useLoadingState, useProStudioViewerRole } from '../../hooks';
import {
  formatDateTime,
  getEnvDotStyle,
  getPlatformDisplayName,
  getPlatformLogo,
  groupChannelsBySolution,
  indexChannelsByPype,
  mapConnectionToRow,
  isSupportedPlatform,
  toChannelEnv,
  type LiveAgentPlatformRow,
  type SolutionChannelGroup,
} from '../../utils';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../../xstate/app.xstate';
import { resolveLiveAgentPlatformsLoadState } from './live-agent-platforms-load-state';
import { PROJECT_SETTINGS_SECTIONS } from './sections';
import { useGoToChannel } from './use-go-to-channel';
import { useGoToSolutionVersions } from './use-go-to-solution-versions';

const section = PROJECT_SETTINGS_SECTIONS[0];

type Env = 'live' | 'sandbox';

const CHANNELS_MAX_WIDTH = 560;

// Shown when the env's catalog holds no supported platform at all, so there is
// nothing the Add form could offer. The integrations themselves are created on
// the platform side, which is why this is not something to fix from here.
//
// Rendered as a VISIBLE callout, never as the disabled button's tooltip. A
// disabled `ps-button` is a native `<button disabled>`: it is out of the tab
// order, so `ps-tooltip`'s focus path can never fire, and the tooltip is not
// linked to the button for a screen reader either (measured: the AX tree shows
// the button and the tooltip with no relation between them). The state also
// reaches users who have rows in the table, where the empty state — the only
// other carrier of this sentence — does not render at all.
const NO_PLATFORMS_HINT =
  'No supported live agent platforms are available for this environment. Contact Center or a LAAPI integration has to exist on the platform first.';

export const LiveAgentPlatforms: FC = () => {
  const [env, setEnv] = useState<Env>('sandbox');
  // Keyed by `groupToggleKey` (connection id + solution id): one entry per
  // expanded solution box, not per table row.
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {}
  );

  // One selector per field, not one returning an object: `useProStudioCtxSelector`
  // compares by reference, so a fresh object literal makes this page (and the
  // modal it renders) re-render on EVERY machine transition, however unrelated.
  const pypeConnections = useProStudioCtxSelector((ctx) => ctx.pypeConnections);
  const liveAgentPypes = useProStudioCtxSelector((ctx) => ctx.liveAgentPypes);
  const agentRoutingSchemas = useProStudioCtxSelector(
    (ctx) => ctx.agentRoutingSchemas
  );
  const solutions = useProStudioCtxSelector((ctx) => ctx.solutions);

  const isViewer = useProStudioViewerRole();

  const { openModal: openPlatformModal, liveAgentPlatformModal } =
    useLiveAgentPlatformModal();
  const { openModal: openDeleteModal, deleteLiveAgentPlatformModal } =
    useDeleteLiveAgentPlatformModal();
  const goToChannel = useGoToChannel();
  const goToSolutionVersions = useGoToSolutionVersions();
  const translateSolutionGroups = useSolutionGroupsTranslate();

  // WithLoadingState mounts this page immediately (it only hides it via CSS
  // while loading), so a mount-time dispatch can fire before the XState
  // machine reaches `idle` — where this event is actually handled — and gets
  // silently dropped with no retry. Gating on `useLoadingState()` too makes
  // the dispatch (re)fire once the machine is actually ready, not just once
  // at mount; `hasLoaded` then prevents re-dispatching after success.
  const isPageLoading = useLoadingState();
  // Gate on the platform catalog too, not just the pypes. `liveAgentPypes` is
  // populated by ~10 other flows (Error Handling, connector create/update/delete),
  // while `agentRoutingSchemas` is only ever set by `loadLiveAgentPlatforms` — so
  // keying off the pypes alone meant that arriving here after any of those left
  // the catalog null and the Add/Edit modal's Platform dropdown empty.
  //
  // Neither half waits for the solutions: the `loadingBots` state loads them,
  // and can leave via `#error` with them still `undefined` — see
  // `resolveLiveAgentPlatformsLoadState`. Until they are known the channels
  // render as a flat list.
  const { catalogLoaded: hasLoaded, readyToRender } =
    resolveLiveAgentPlatformsLoadState({
      pypeConnections,
      liveAgentPypes,
      agentRoutingSchemas,
    });
  useEffect(() => {
    if (!isPageLoading && !hasLoaded) {
      sendProStudioEvent({ type: 'loadLiveAgentPlatforms' });
    }
  }, [isPageLoading, hasLoaded]);

  /**
   * Whether this env offers anything to pick. Since PSTUDIO-4072 the picker is
   * narrowed to Contact Center + `LAAPI_*`, so a project whose integrations are
   * all legacy now has an EMPTY offer — and an empty `Select` behind an enabled
   * Add button is a dead end: `required` blocks the submit with no message.
   * Before the filter the list was never empty.
   */
  const noPlatformsAvailable = useMemo(
    () =>
      hasLoaded &&
      !(agentRoutingSchemas?.[env] ?? []).some((schema) =>
        isSupportedPlatform(schema.agent_routing)
      ),
    [hasLoaded, agentRoutingSchemas, env]
  );

  // Stable identity so it can be a dependency of the memoized `columns` below.
  const toggleExpanded = useCallback(
    (key: string) =>
      setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] })),
    []
  );

  // Derive the env's channels once, not per row: `getChannelsFromPypes` walks
  // every pype's streams and widgets, and this component re-renders on unrelated
  // state (expanding a row's channel list, switching env).
  const channelsByPype = useMemo(
    () => indexChannelsByPype(liveAgentPypes?.[env] ?? []),
    [liveAgentPypes, env]
  );

  // One row per connection, scoped by the connection's own `environment`. The
  // index only supplies the names behind each bound pype id — the binding itself
  // is the connection's `pypes` array.
  const rows: LiveAgentPlatformRow[] = useMemo(
    () =>
      (pypeConnections ?? [])
        .filter((connection) => toChannelEnv(connection.environment) === env)
        .map((connection) => mapConnectionToRow(connection, channelsByPype)),
    [pypeConnections, env, channelsByPype]
  );

  // Every row's solution boxes, built once per (rows, solutions) — not per cell
  // render. The resolver (the channel -> solution index) is built once per
  // `solutions` and shared by all rows; it is `null` while the solutions are
  // unknown, which makes each row one flat, unresolved list instead of a false
  // "No solution" (`useChannelGroupingResolver`, shared with the Edit modal).
  const resolveSolution = useChannelGroupingResolver(solutions);
  const groupsByConnectionId = useMemo(
    () =>
      new Map<string, SolutionChannelGroup[]>(
        rows.map((row) => [
          row.id,
          groupChannelsBySolution(row.channels, resolveSolution),
        ])
      ),
    [rows, resolveSolution]
  );

  // Memoized: TanStack rebuilds its column model whenever this array's identity
  // changes, so an unmemoized literal re-created the whole table on every render
  // of a component that re-renders on unrelated state.
  const columns: ColumnDef<LiveAgentPlatformRow>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        accessorFn: (row) => row.name,
        header: 'Connection Name',
        cell: ({ row }) =>
          html`<div style="min-width: 200px;">
            <ps-text-subtitle size="small">
              ${row.original.name}
            </ps-text-subtitle>
          </div>`,
      },
      {
        accessorKey: 'agentRouting',
        header: 'Platform',
        // The platform icon comes straight off the connection's `platformId` — no
        // config parsing and no fallback to a pype avatar, since the connection
        // records which platform it is.
        //
        // The name goes on the image's `alt`, not only into the tooltip: the row
        // is focusable and exposes `role="button"`, so its accessible name is
        // built from the cells' contents — and a tooltip does not exist for a
        // keyboard or screen-reader user (WCAG 1.1.1 / 4.1.2). It also matters
        // visually: platforms without their own asset share the Pypestream mark,
        // which alone cannot tell two connections apart.
        cell: ({ row }) => {
          const platformName = getPlatformDisplayName(row.original.platformKey);
          // Falls back to the connection name for the TOOLTIP only. `alt` stays
          // empty in that case: with no `platformId` the image carries no
          // information the row does not already state, and repeating the name
          // would have a screen reader read the row's own title twice.
          const tooltip = platformName || row.original.name;

          return html`<ps-stack justifyContent="center">
            <ps-tooltip content=${tooltip}>
              <ps-image
                width="24"
                height="24"
                alt=${platformName}
                src=${getPlatformLogo(row.original.platformKey)}
              ></ps-image>
            </ps-tooltip>
          </ps-stack>`;
        },
        meta: { align: 'center' },
      },
      {
        accessorKey: 'channels',
        header: 'Channels Using It',
        cell: ({ row }) => {
          const { id } = row.original;

          return html`<div style=${`max-width: ${CHANNELS_MAX_WIDTH}px;`}>
            ${renderSolutionChannelGroups({
              groups: groupsByConnectionId.get(id) ?? [],
              ownerId: id,
              isExpanded: (key) => !!expandedGroups[key],
              onToggle: toggleExpanded,
              onGoToChannel: goToChannel,
              onGoToSolution: goToSolutionVersions,
              testIdPrefix: SOLUTION_GROUPS_TEST_ID_PREFIX.table,
              translate: translateSolutionGroups,
            })}
          </div>`;
        },
      },
      {
        accessorKey: 'lastUpdated',
        header: 'Last Updated',
        cell: ({ row }) => {
          const { lastUpdated } = row.original;
          const label = lastUpdated
            ? formatDateTime(new Date(lastUpdated).toISOString())
            : '—';
          return html`<ps-stack justifyContent="end">
            <ps-text-body size="small" truncate>${label}</ps-text-body>
          </ps-stack>`;
        },
        meta: { align: 'end' },
      },
      // The kebab holds nothing but Edit and Delete, so a viewer gets no actions
      // column at all — same as channels.tsx, which drops its row actions rather
      // than rendering an empty menu.
      ...(isViewer
        ? []
        : [
            {
              id: 'actions',
              header: () =>
                html`<span style="opacity: 0; font-size: 4px;">Actions</span>`,
              cell: ({ row }) => html`
                <ps-stack justifyContent="end">
                  <ps-icon-button
                    name="kebab"
                    size="small"
                    id=${`live-agent-actions-${row.original.id}`}
                  ></ps-icon-button>
                </ps-stack>
                <ps-dropdown
                  trigger=${`live-agent-actions-${row.original.id}`}
                  placement="bottom-end"
                >
                  <ps-menu>
                    <ps-menu-item
                      test-id=${`ps-live-agent-platforms-edit-${row.original.id}`}
                      @click=${() =>
                        openPlatformModal('edit', {
                          env,
                          connectionId: row.original.id,
                        })}
                    >
                      <ps-icon name="edit" size="small" slot="prefix"></ps-icon>
                      Edit
                    </ps-menu-item>
                    <ps-menu-item
                      test-id=${`ps-live-agent-platforms-delete-${row.original.id}`}
                      variant="danger"
                      @click=${() =>
                        openDeleteModal({
                          connectionId: row.original.id,
                          connectionName: row.original.name,
                          env,
                        })}
                    >
                      <ps-icon
                        name="delete"
                        size="small"
                        slot="prefix"
                      ></ps-icon>
                      Delete
                    </ps-menu-item>
                  </ps-menu>
                </ps-dropdown>
              `,
              meta: { align: 'end' },
            } as ColumnDef<LiveAgentPlatformRow>,
          ]),
    ],
    [
      expandedGroups,
      groupsByConnectionId,
      env,
      isViewer,
      goToChannel,
      goToSolutionVersions,
      translateSolutionGroups,
      toggleExpanded,
      openPlatformModal,
      openDeleteModal,
    ]
  );

  // Disabled while the catalog is still in flight, too: the button renders
  // outside the loader branch, so before `hasLoaded` it would open a modal whose
  // picker is empty — the same dead end the filter's guard exists to prevent.
  // `data-privacy.tsx` gates its own create action the same way.
  const addPlatformButton = (
    <Button
      test-id="ps-live-agent-platforms-add"
      size="large"
      disabled={!hasLoaded || noPlatformsAvailable}
      onClick={() => openPlatformModal('add', { env })}
    >
      <Icon name="add" slot="prefix" />
      Add Platform
    </Button>
  );

  return (
    <PageSection size="small" test-id="ps-live-agent-platforms-section">
      <Stack alignItems="center" justifyContent="space-between" nowrap>
        <TextTitle size="small">
          <TranslationComponent i18nKey={section.i18nKey as TranslationKeys}>
            {section.label}
          </TranslationComponent>
        </TextTitle>

        <Stack alignItems="center" gutter="medium" nowrap>
          <SegmentedControl
            test-id="ps-live-agent-platforms-env"
            fitContent
            value={env}
            onChange={(e) => setEnv(e.detail.value as Env)}
          >
            <SegmentedControlItem value="sandbox">
              <span
                slot="prefix"
                style={getEnvDotStyle('sandbox', env === 'sandbox')}
              />
              Sandbox
            </SegmentedControlItem>
            <SegmentedControlItem value="live">
              <span
                slot="prefix"
                style={getEnvDotStyle('live', env === 'live')}
              />
              Live
            </SegmentedControlItem>
          </SegmentedControl>

          {/*
            Rendered unwrapped in every state. Wrapping it only while disabled
            changes the element's position in the tree, so React unmounts and
            remounts the `ps-button` on each flip — which drops focus if it was
            on the button and leaks a `ps-portal-destination` per mount (the DS
            tooltip never removes its portal on disconnect). The reason lives in
            the callout below instead.
          */}
          {!isViewer && addPlatformButton}
        </Stack>
      </Stack>

      <Spacer size="xlarge" />

      {/*
        The empty-state/table swap MUST stay inside this wrapper. The modals below
        are `ps-modal`s, which relocate themselves into a portal under
        `document.body` while open — React doesn't know, so swapping a sibling
        *before* them makes it insert against a node that is no longer a child of
        this section, and the DOM throws ("insertBefore ... not a child of this
        node"). Creating a platform did exactly that: open the modal, then submit,
        and the branch flips. See portaled-modal-sibling.test.tsx.
      */}
      <div>
        {/*
          `role="status"` so switching Sandbox/Live — which is what flips this
          state — is announced; the segmented control keeps focus, so nothing
          else would tell a screen-reader user that Add just went away.

          The region is ALWAYS mounted and only its CONTENT is conditional: a
          live region that appears together with its text is not reliably
          announced (WCAG 4.1.3), so putting the role on the `Callout` itself
          made the one moment it exists for the one moment it could not cover.
          Same shape as `BlockLoadState` in the channel-settings form.

          Kept INSIDE this wrapper for the same reason the swap below is: the
          modals are portaled siblings of it.
        */}
        <div role="status">
          {noPlatformsAvailable && (
            <>
              <Callout
                size="small"
                theme="light"
                test-id="ps-live-agent-platforms-no-platforms"
              >
                <Stack alignItems="center" gutter="xsmall">
                  <Icon name="info" size="small" weight="medium" />
                  <CalloutTitle>{NO_PLATFORMS_HINT}</CalloutTitle>
                </Stack>
              </Callout>
              <Spacer size="large" />
            </>
          )}
        </div>
        {!readyToRender ? (
          <SettingsSectionLoader testId="ps-live-agent-platforms-loading" />
        ) : rows.length === 0 ? (
          <SettingsEmptyState
            testId="ps-live-agent-platforms-empty"
            title="No live agent platforms yet"
            description={
              noPlatformsAvailable
                ? NO_PLATFORMS_HINT
                : `Add a platform to let ${env === 'live' ? 'Live' : 'Sandbox'} channels escalate conversations to a human agent.`
            }
          />
        ) : (
          <DataTable
            test-id="ps-live-agent-platforms-table"
            filterColumns={['name']}
            size="small"
            columns={columns}
            data={rows}
            actionable={false}
          />
        )}
      </div>

      {liveAgentPlatformModal}
      {deleteLiveAgentPlatformModal}
    </PageSection>
  );
};
